import PropTypes from "prop-types";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Divider, Popconfirm, Row, Tag, Tooltip } from "antd";
import Button from "../../atoms/Button";
import BedIcon from "@/assets/svg/BedIcon";
import SqftIcon from "@/assets/svg/SqftIcon";
import LocationIcon from "@/assets/svg/LocationIcon";
import formatAddress from "@/utils/propertyAddress";
import Share from "../Share";
import { showModal } from "@/store/slices/modalSlice";
import StarIcon from "@/assets/svg/StarIcon";
import DeleteIcon from "@/assets/svg/DeleteIcon";
import EditIcon from "@/assets/svg/EditIcon";
import EyeSlashIcon from "@/assets/svg/EyeSlashIcon";
import { EyeOutlined, ShareAltOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { imgPath } from "@/constants/miscConstants";
import HeartOutlineIcon from "@/assets/svg/fav/HeartOutlineIcon";
import EyeOutlineIcon from "@/assets/svg/EyeOutlineIcon";
import Counter from "@/common/atoms/Counter";
import ListingFeature from "../PaymentPlan/ListingFeature";
import FeaturedRibbon from "../FeaturedRibbon";
import AppBadge from "@/common/atoms/Badge";
import CurrencyExchange from "@/common/atoms/lable/CurrencyExchange";
import {
  generatePropertyHomeTypeMap,
  getPropertyHomeTypeList,
  getTranslatedAreaUnit
} from "@/utils";
import { useTranslation } from "react-i18next";
import BathroomIcon from "@/assets/svg/BathroomIcon";

const PropertyManagementCard = ({ propertyItems, onDelete, onHide }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { propertyDeleteLoading, propertyHideLoading } = useSelector(
    (state) => state.property,
    shallowEqual
  );

  const handleDelete = () => {
    if (onDelete) {
      onDelete(propertyItems?.uuid);
    }
  };

  const handleHide = () => {
    if (onHide) {
      onHide(propertyItems?.uuid);
    }
  };

  const showShareModal = () => {
    dispatch(
      showModal({
        content: (
          <Share
            image={propertyItems?.propertyThumbnailUrl}
            shareValueUUID={propertyItems?.uuid}
            shareURL="property-detail"
          />
        ),
        classes: "share-modal-container"
      })
    );
  };
  const showFeatureModal = () => {
    dispatch(
      showModal({
        content: <ListingFeature propertyId={propertyItems?.uuid} />,
        classes: "feature-listings-modal-container"
      })
    );
  };

  const handleEditProperty = () => {
    navigate(`/manage/edit-property/${propertyItems?.uuid}`);
  };
  const handleOnClickImage = () => {
    navigate(`/property-detail/${propertyItems?.uuid}`);
  };

  const hideAndShowButtonText = propertyItems?.hideProperty
    ? `${t("common.show")}`
    : `${t("common.hide")}`;
  const isLoading = propertyHideLoading || propertyDeleteLoading;

  const translatedUnit = getTranslatedAreaUnit(
    propertyItems?.propertySizePostfix,
    t
  );

  const list = getPropertyHomeTypeList(t);
  const propertyHomeTypeMap = generatePropertyHomeTypeMap(list);

  return (
    <div className="bg-white rounded-xl my-4 p-2 ltr:sm:!pr-6 rtl:sm:!pl-6 sm:p-2 relative">
      <Row>
        <img
          src={propertyItems?.propertyThumbnailUrl || imgPath}
          className="w-full sm:w-64 h-44 ltr:md:mr-6 rtl:md:ml-6 object-center object-cover rounded-xl cursor-pointer"
          onClick={handleOnClickImage}
        />
        {propertyItems?.isFeatured && (
          <FeaturedRibbon text={t("propertyDetails.featured")} />
        )}

        <div className="px-0 flex flex-col w-full lg:w-1/2">
          <div className="flex items-center gap-4">
            <AppBadge
              propertyListingType={propertyItems?.propertyListingType}
              classes={`mt-2`}
            />
            <Tag className="border-[#DADFE6] bg-white text-[#011B3B] font-sans">
              {propertyHomeTypeMap[propertyItems?.propertyHomeType]}
            </Tag>
          </div>

          <Tooltip title={propertyItems?.name} placement="topLeft">
            <p className="text-xl my-2 font-manropeBold text-[#32493B]">
              {propertyItems?.name}
            </p>
          </Tooltip>
          <div className="flex items-center">
            <LocationIcon width="14px" height="14px" />
            <Tooltip title={formatAddress(propertyItems)} placement="topLeft">
              <p className="truncate px-2 text-[#596460]">
                {formatAddress(propertyItems)}
              </p>
            </Tooltip>
          </div>

          <div className="flex items-center w-full mt-8 mb-2 flex-wrap sm:flex-nowrap">
            <div className="ltr:border-r-[1px] rtl:border-l-[1px] ltr:pr-4 rtl:pl-4 border-[#DDE5F1]">
              <CurrencyExchange propertyPrice={propertyItems?.propertyPrice} />
            </div>
            <div className="flex items-center px-2 ltr:pl-4 rtl:pr-4">
              <BedIcon color="#010F0A" />
              <p className="truncate px-2">
                {propertyItems?.noBedroom || 0} {t("card.bed")}
              </p>
            </div>
            <div className="flex items-center px-0 md:px-2 ltr:md:pl-4 rtl:md:pr-4">
              <BathroomIcon color="#010F0A" />
              <p className="truncate px-2">
                {propertyItems?.noBathroom || 0} {t("card.bath")}
              </p>
            </div>
            <div className="flex items-center w-auto sm:mt-0 ltr:md:pl-4 rtl:md:pr-4">
              <SqftIcon color="#010F0A" />
              <p className="truncate px-2 border-secondary">
                {`${propertyItems?.propertySize.toLocaleString()} / ${translatedUnit}`}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-auto" />

        <div className="flex flex-wrap flex-col justify-between lg:gap-6 lg:justify-end xl:justify-between lg:flex-row xl:flex-col xl:items-end items-start xl:mt-0 mt-4 w-full xl:w-auto">
          <div className="flex justify-between w-auto gap-4">
            <Counter
              count={propertyItems?.favoriteCount}
              icon={<HeartOutlineIcon fill="#6D7588" />}
            />
            <Counter
              count={propertyItems?.totalViews}
              icon={<EyeOutlineIcon fill="#6D7588" />}
            />
          </div>
          <div className="flex justify-between w-full sm:w-auto gap-4 mt-5 lg:mt-0">
            <Button
              type="solid"
              className="!bg-black !w-full hover:!text-white disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={
                isLoading ||
                propertyItems?.isFeatured ||
                propertyItems?.hideProperty
              }
              onClick={showFeatureModal}
              icon={<StarIcon className="group-hover:text-white" />}
              iconPosition="start">
              {t("myProperties.promoteThisAd")}
            </Button>
          </div>
          <div className="flex flex-wrap flex-row lg:justify-end items-start lg:items-end mt-4 mb-2 lg:mt-0 gap-2 w-auto">
            <Button
              type="outline"
              className="!px-0 !text-[#011B3B] !border-none hover:!shadow-none disabled:!bg-transparent disabled:!text-opacity-60"
              onClick={handleEditProperty}
              icon={<EditIcon className="group-hover:text-white" />}
              iconPosition="start"
              disabled={isLoading || propertyItems?.hideProperty}>
              {t("common.edit")}
            </Button>
            <Divider type="vertical" className="border-[#DDE5F1] m-auto" />
            <Button
              type="outline"
              className="!px-0 !text-[#011B3B] !border-none hover:!shadow-none disabled:!bg-transparent disabled:!text-opacity-60"
              onClick={showShareModal}
              icon={<ShareAltOutlined className="group-hover:text-white" />}
              iconPosition="start"
              disabled={isLoading || propertyItems?.hideProperty}>
              {t("myProperties.share")}
            </Button>
            <Divider type="vertical" className="border-[#DDE5F1] m-auto" />
            <Popconfirm
              title={`${hideAndShowButtonText} ${t("common.property")}`}
              description={`${t("myProperties.confirmHideAndShowMessage", {
                propertyStatus: hideAndShowButtonText
              })}`}
              okText={t("common.ok")}
              cancelText={t("common.cancel")}
              onConfirm={handleHide}
              placement="topRight"
              okButtonProps={{
                loading: propertyHideLoading
              }}>
              <div>
                <Button
                  type="outline"
                  className="!px-0 !text-[#011B3B] !border-none hover:!shadow-none disabled:!bg-transparent disabled:!text-opacity-60"
                  icon={
                    propertyItems?.hideProperty ? (
                      <EyeOutlined className="text-lg" />
                    ) : (
                      <EyeSlashIcon className="group-hover:text-white" />
                    )
                  }
                  iconPosition="start"
                  disabled={isLoading}>
                  {hideAndShowButtonText}
                </Button>
              </div>
            </Popconfirm>
            <Divider type="vertical" className="border-[#DDE5F1] m-auto" />
            <Popconfirm
              title={t("common.delete")}
              description={t("myProperties.delete.confirmMessage")}
              okText={t("common.ok")}
              cancelText={t("common.cancel")}
              onConfirm={handleDelete}
              okButtonProps={{
                loading: propertyDeleteLoading
              }}>
              <div>
                <Button
                  type="outline"
                  className="!px-0 !text-[#C8102E] !border-none hover:!shadow-none disabled:!bg-transparent disabled:!text-opacity-60"
                  icon={<DeleteIcon className="group-hover:text-white" />}
                  iconPosition="start"
                  disabled={isLoading}>
                  {t("common.delete")}
                </Button>
              </div>
            </Popconfirm>
          </div>
        </div>
      </Row>
    </div>
  );
};

const { object, func } = PropTypes;

PropertyManagementCard.propTypes = {
  propertyItems: object,
  onDelete: func,
  onHide: func
};

export default PropertyManagementCard;
